/**
 * OfferVersionSnapshotView
 *
 * Read-only preview of a published offer version snapshot, sourced from
 * GET /offers/:id/versions/:versionId plus GET .../unit-decisions (the
 * persisted per-unit decisions read path, see below) — no live draft
 * endpoints are called. The snapshot endpoint returns the frozen
 * `snapshotData.units` plus a live-enriched
 * `enrichedUnits` sibling (coordinates, dimensions, photo, availability — same
 * source as getClientView), so map pins match what the client actually sees.
 *
 * Renders the exact same shared client-offer-review-v2 shell as the authenticated
 * (/offers/:offerId/client) and public (/public/offers/:token) client offer pages
 * via SharedClientOfferContent, in `previewMode` — every interactive control
 * (per-unit decision buttons, accept footer) stays visible but disabled with a
 * hover tooltip, and no mutation hooks are wired (a historical snapshot has no
 * accept/decision endpoints).
 *
 * Passes `frozenDimensions` so widthM/heightM are parsed from the frozen
 * snapshot text instead of sourced from live `enrichedUnits` (unlike photo/
 * coordinates, which stay live everywhere) — a historical version's physical
 * dimensions must stay immutable even if a unit's live size changed since
 * this version was published. See map-client-view-to-review-unit.ts's
 * useFrozenDimensions doc comment.
 *
 * Also fetches the version's persisted per-unit client decisions
 * (`useUnitDecisions`, client-offer-decision-persistence — the same read
 * path the "Decyzje klienta" tab uses) and maps them onto `decisions`, so
 * the shell's accepted/rejected/pending summary and accepted value reflect
 * what the client actually decided instead of defaulting every unit to
 * pending with a zero summary (CU-86cb5z48j).
 *
 * Traceability: REQ-7.6 → offer-versioning / Read-only Snapshot View UI
 */
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { offerVersionEndpoints } from '@ooh/contracts';
import type {
  ClientOfferViewUnit,
  OfferVersionSnapshotWithEnrichment,
  UnitDecisionsListItem,
} from '@ooh/contracts';
import type { ClientDecision } from '@ooh/ui/components/client-offer-view';
import { useApiSuspenseQuery } from '../../data/use-api-suspense-query';
import { QueryBoundary } from '../../layouts/query-boundary';
import { SectionSkeleton } from '../../layouts/skeletons';
import { useCurrentUser } from '../../plugs/current-user';
import type { OffersT } from '../client-offer/client-offer-view-labels';
import { buildRowIdByMediaUnitId } from '../client-offer/lib/seed-selections-from-prefill';
import {
  SharedClientOfferContent,
  type SharedClientOfferContentProps,
} from '../client-offer/shared-client-offer-content';
import { unitDecisionsRequest } from './api/queries';
import { SnapshotBackLink } from './snapshot-back-link';

// ─── No-op preview stubs ──────────────────────────────────────
// A historical version snapshot has no accept/reject/decision endpoints —
// these stubs satisfy SharedClientOfferContentProps without wiring any
// mutation hook. previewMode disables every control that would call them.

function noop(): void {}

const NOOP_ACCEPT_MUTATION: SharedClientOfferContentProps['acceptMutation'] = {
  isPending: false,
  isSuccess: false,
  isError: false,
  error: undefined,
};

// ─── Sub-components ───────────────────────────────────────────

/**
 * Renders the archival banner and/or the decisions-fetch-error banner. Both
 * are optional and independent — a SUPERSEDED version whose decisions fetch
 * also failed shows both stacked. Without the error banner, a failed
 * unit-decisions fetch would silently render the same all-pending, zero
 * summary this component exists to fix (CU-86cb5z48j), indistinguishable
 * from a version the client genuinely has not decided on yet.
 */
function SnapshotTopBanner({
  isArchival,
  decisionsFailed,
  versionNumber,
  t,
}: {
  isArchival: boolean;
  decisionsFailed: boolean;
  versionNumber: number;
  t: OffersT;
}) {
  if (!isArchival && !decisionsFailed) return undefined;
  return (
    <>
      {isArchival ? (
        <div
          className="border-b border-amber-200 bg-amber-50 text-amber-900"
          data-testid="snapshot-archival-banner"
        >
          <div className="mx-auto max-w-4xl px-6 py-3 text-sm font-medium">
            {t('workspace.versions.snapshot.archivalBanner', { number: versionNumber })}
          </div>
        </div>
      ) : null}
      {decisionsFailed ? (
        <div
          className="border-b border-destructive/30 bg-destructive/10 text-destructive"
          data-testid="snapshot-decisions-error-banner"
        >
          <div className="mx-auto max-w-4xl px-6 py-3 text-sm font-medium">
            {t('workspace.versions.snapshot.decisionsErrorBanner')}
          </div>
        </div>
      ) : null}
    </>
  );
}

// ─── Persisted decision mapping (client-offer-decision-persistence) ───────
// unitDecisions keys by mediaUnitId (the stable identifier — see
// offer-version-unit-decisions.ts), while the shell keys `decisions` by the
// snapshot row id. Reuses buildRowIdByMediaUnitId's mediaUnitId -> row id
// lookup convention (seed-selections-from-prefill.ts) rather than
// re-deriving it, so the two call sites can't diverge. UNDECIDED entries
// are omitted — matching that same module's convention of leaving pending
// rows absent from the map.

function buildDecisionsFromUnitDecisions(
  items: UnitDecisionsListItem[],
  rowIdByMediaUnitId: Map<number, string>,
): Record<string, ClientDecision> {
  const decisions: Record<string, ClientDecision> = {};
  for (const item of items) {
    if (item.decision === 'UNDECIDED') continue;
    const rowId = rowIdByMediaUnitId.get(item.mediaUnitId);
    if (rowId == null) continue;
    decisions[rowId] = item.decision === 'ACCEPTED' ? 'accepted' : 'rejected';
  }
  return decisions;
}

// ─── Main component ───────────────────────────────────────────

export interface OfferVersionSnapshotViewProps {
  offerId: string;
  snapshot: OfferVersionSnapshotWithEnrichment;
  onOpenUnitDetail: (mediaUnitId: number) => void;
}

function SnapshotContent({
  offerId,
  snapshot,
  onOpenUnitDetail,
  decisionItems,
  decisionsFailed,
}: OfferVersionSnapshotViewProps & {
  decisionItems: UnitDecisionsListItem[];
  decisionsFailed: boolean;
}) {
  const { t } = useTranslation('offers');
  const { user } = useCurrentUser();
  const {
    versionNumber,
    status,
    publishedAt,
    acceptedAt,
    rejectedAt,
    snapshotData,
    enrichedUnits,
  } = snapshot;

  // Lookup of live-enriched unit by snapshot row id, mirroring client-offer-page.tsx's
  // enrichedById pattern — O(1) access when mapping each snapshot unit below.
  const enrichedById = React.useMemo(() => {
    const map = new Map<number, ClientOfferViewUnit>();
    for (const u of enrichedUnits) {
      map.set(u.id, u);
    }
    return map;
  }, [enrichedUnits]);

  const decisions = React.useMemo(
    () => buildDecisionsFromUnitDecisions(decisionItems, buildRowIdByMediaUnitId(enrichedById)),
    [decisionItems, enrichedById],
  );

  const previewHint = t('workspace.versions.snapshot.previewHint');
  // Only a SUPERSEDED version is a true historical/archival snapshot — a
  // PUBLISHED or ACCEPTED version is still the offer's active version, so the
  // archival banner must not show for it. See mark-version-superseded query
  // (packages/db) — only PUBLISHED rows transition to SUPERSEDED on republish.
  const isArchival = status === 'SUPERSEDED';

  return (
    <div
      className="flex min-h-screen flex-col bg-background"
      data-testid="offer-version-snapshot-page"
    >
      <SharedClientOfferContent
        versionNumber={versionNumber}
        publishedAt={publishedAt}
        acceptedAt={acceptedAt ?? null}
        rejectedAt={rejectedAt ?? null}
        diff={null}
        onAccept={noop}
        acceptMutation={NOOP_ACCEPT_MUTATION}
        onOpenUnitDetail={onOpenUnitDetail}
        clientName={snapshotData.clientName}
        offerTitle={snapshotData.title}
        previewMode
        previewHint={previewHint}
        topBanner={
          <SnapshotTopBanner
            isArchival={isArchival}
            decisionsFailed={decisionsFailed}
            versionNumber={versionNumber}
            t={t}
          />
        }
        snapshotUnits={snapshotData.units}
        enrichedUnitsById={enrichedById}
        decisions={decisions}
        frozenDimensions
        showSupplierIdentity
        campaignFrom={snapshotData.defaultDateFrom ? new Date(snapshotData.defaultDateFrom) : null}
        campaignTo={snapshotData.defaultDateTo ? new Date(snapshotData.defaultDateTo) : null}
        accessMode="Zalogowany (SSO)"
        loggedInEmail={user?.email ?? ''}
      />

      <div className="border-t border-border">
        <div className="mx-auto w-full max-w-4xl px-6 py-4">
          <SnapshotBackLink offerId={offerId} label={t('workspace.versions.snapshot.backLink')} />
        </div>
      </div>
    </div>
  );
}

function SnapshotWithDecisions(props: OfferVersionSnapshotViewProps) {
  const { data } = useApiSuspenseQuery(
    offerVersionEndpoints.unitDecisions,
    unitDecisionsRequest(props.offerId, props.snapshot.id),
  );
  return <SnapshotContent {...props} decisionItems={data} decisionsFailed={false} />;
}

export function OfferVersionSnapshotView(props: OfferVersionSnapshotViewProps) {
  return (
    <QueryBoundary
      fallback={<SectionSkeleton />}
      renderError={() => <SnapshotContent {...props} decisionItems={[]} decisionsFailed />}
    >
      <SnapshotWithDecisions {...props} />
    </QueryBoundary>
  );
}
